import { Modal } from "@smart/itops-sb-design-system-dom";

type ReminderScheduleConfirmModalProps = {
  open: boolean;
  isTurningOn: boolean;
  isTurningOff?: boolean;
  scheduledFormCount?: number;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const applyBody = (scheduledFormCount: number) =>
  scheduledFormCount > 0
    ? `${scheduledFormCount} ${scheduledFormCount === 1 ? "form" : "forms"} currently ${scheduledFormCount === 1 ? "has" : "have"} reminders scheduled. They'll move to your new settings from here, which can take up to 24 hours to apply.`
    : "No forms currently have reminders scheduled. Your new settings will apply from here, which can take up to 24 hours.";

const turnOffBody = (scheduledFormCount: number) =>
  scheduledFormCount > 0
    ? `${scheduledFormCount} ${scheduledFormCount === 1 ? "form has" : "forms have"} reminders scheduled. Turning this off can take up to 24 hours to apply, so send any reminders manually from now.`
    : "No forms have reminders scheduled. Turning this off can take up to 24 hours to apply, so send any reminders manually from now.";

const buildContent = (
  isTurningOn: boolean,
  isTurningOff: boolean | undefined,
  scheduledFormCount: number,
) => {
  if (isTurningOff) {
    return {
      header: "Turn off scheduled reminders",
      body: turnOffBody(scheduledFormCount),
      confirmLabel: "Confirm",
    };
  }

  if (isTurningOn) {
    return {
      header: "Confirm schedule",
      body: "Your reminder schedule will start applying to forms shared from here, which can take up to 24 hours to take effect.",
      confirmLabel: "Confirm",
    };
  }

  return {
    header: "Apply changes to reminders",
    body: applyBody(scheduledFormCount),
    confirmLabel: "Apply",
  };
};

export const ReminderScheduleConfirmModal = ({
  open,
  isTurningOn,
  isTurningOff,
  scheduledFormCount = 0,
  loading,
  onCancel,
  onConfirm,
}: ReminderScheduleConfirmModalProps) => {
  const { header, body, confirmLabel } = buildContent(
    isTurningOn,
    isTurningOff,
    scheduledFormCount,
  );

  return (
    <Modal
      dataTestId="reminder-schedule-confirm-modal"
      open={open}
      onClose={onCancel}
      loading={loading}
      header={{ text: header }}
      footer={{
        buttons: [
          {
            text: "Cancel",
            type: "button",
            variant: "secondarySubtle",
            disabled: loading,
            onClick: onCancel,
          },
          {
            text: confirmLabel,
            type: "button",
            variant: "primaryDefault",
            disabled: loading,
            onClick: onConfirm,
            dataTestId: "reminder-schedule-confirm",
          },
        ],
      }}
    >
      <p>{body}</p>
    </Modal>
  );
};
